import * as React from 'react'

import { Box, theme } from '@factorialco/deprecated-design-system'
import {
  CustomVisualizationProps,
  OneDataCollection,
  Spinner,
  useDataCollectionData,
} from '@factorialco/f0-react/dist/experimental'
import { Calendar } from '@factorialco/f0-react/icons/app'
import { TeamViewEmployeesDocument } from 'generated/resources/graphql'
import type moment from 'moment'

import { observer } from 'mobx-react'

import { DateNavigator } from 'modules/core/components/DateNavigator'
import { useUnsafeAsyncData } from 'modules/core/hooks/useAsyncData'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { i18nDateToString } from 'modules/core/lib/dates'

import Exports from 'modules/calendar/components/Exports'
import type { CalendarRootRenderProps } from 'modules/calendar/lib/RouteContextParams'
import generateFetchData from 'modules/calendar/lib/generateFetchData'
import calendarEvents from 'modules/calendar/store/collections/calendarEvents'
import { useEmployeeFilters } from 'modules/employees/lib/filters'
import employees from 'modules/employees/store/collections/employees'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

import TeamViewTable from './components/TeamViewTable'

type Props = CalendarRootRenderProps & {
  lastLeaveUpdatedInMilliseconds: number
}

type TeamViewSource = ReturnType<typeof useTeamViewSource>

const useTeamViewSource = (
  companyId: number,
  hasJobCatalogTreeFeature: boolean,
  date: string,
  employeeFilters: ReturnType<typeof useEmployeeFilters>['filters']
) => {
  return useGraphqlDataCollectionSource(
    {
      document: TeamViewEmployeesDocument,
      perPage: 20,
      paginationType: 'infinite-scroll',
      mapVariables: ({ filters, search }) => {
        const teamIds = filters.team?.map((id) => id)
        const locationIds = filters.workplace?.map((id) => id)
        const legalEntityIds = filters.legalEntity?.map((id) => id)
        const jobLevelIds = !hasJobCatalogTreeFeature
          ? filters.jobRole?.map((id) => Number(id))
          : undefined
        const jobCatalogTreeNodeIds = hasJobCatalogTreeFeature
          ? filters.jobRole?.map((id) => id.toString())
          : undefined
        const reportingTo = filters.reportingTo?.map((id) => id)
        const timeoffManagerIds = filters.timeoffManager?.map((id) => id)

        return {
          companyId,
          search: search,
          teamIds: teamIds?.length ? teamIds : undefined,
          locationIds: locationIds?.length ? locationIds : undefined,
          legalEntityIds: legalEntityIds?.length ? legalEntityIds : undefined,
          referenceContractLevelIds: jobLevelIds?.length ? jobLevelIds : undefined,
          referenceContractTreeLevelUuids: jobCatalogTreeNodeIds?.length
            ? jobCatalogTreeNodeIds
            : undefined,
          reportingTo: reportingTo?.length ? reportingTo : undefined,
          timeoffManagerIds: timeoffManagerIds?.length ? timeoffManagerIds : undefined,
          activeOn: date,
        }
      },
      mapPagination: (data) => {
        const connection = data.employees.employeesConnection
        type ConnectionWithPageInfo = typeof connection & {
          pageInfo?: { endCursor?: string | null; hasNextPage?: boolean }
        }
        const connectionWithPageInfo: ConnectionWithPageInfo = connection
        return {
          total: connection.totalCount ?? 0,
          cursor: connectionWithPageInfo.pageInfo?.endCursor ?? undefined,
          hasNextPage: connectionWithPageInfo.pageInfo?.hasNextPage ?? false,
        }
      },
      mapData: (data) => data.employees.employeesConnection.nodes,
      filters: employeeFilters,
    },
    [companyId, hasJobCatalogTreeFeature, date]
  )
}

interface TeamViewVisualizationProps {
  source: TeamViewSource
  date: moment.Moment
  changingData: boolean
  children: React.ReactNode
}

const TeamViewVisualization = observer(function TeamViewVisualization({
  source,
  date,
  changingData,
  children,
}: TeamViewVisualizationProps) {
  const { data, isLoading, isLoadingMore, loadMore, paginationInfo } = useDataCollectionData(source)
  const scrollContainerRef = React.useRef<HTMLDivElement>(null)

  const hasMore = paginationInfo && 'hasMore' in paginationInfo ? paginationInfo.hasMore : false

  React.useEffect(() => {
    const container = scrollContainerRef.current
    if (!container || isLoading || isLoadingMore || !hasMore) return

    if (container.scrollHeight <= container.clientHeight) {
      loadMore()
    }
  }, [data.records.length, isLoading, isLoadingMore, hasMore, loadMore])

  const handleScroll = React.useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      if (isLoadingMore || !hasMore) return

      const target = event.currentTarget
      const { scrollTop, scrollHeight, clientHeight } = target
      // Use 20% of viewport height as threshold - works better across different screen sizes
      const scrollThreshold = clientHeight * 0.2

      if (scrollHeight - scrollTop - clientHeight < scrollThreshold) {
        loadMore()
      }
    },
    [isLoadingMore, hasMore, loadMore]
  )

  const filteredEmployeeIds = React.useMemo(() => {
    return new Set(data.records.map((emp) => emp.id))
  }, [data.records])

  const filteredEmployees = React.useMemo(() => {
    if (filteredEmployeeIds.size === 0 && !isLoading) {
      return []
    }
    if (filteredEmployeeIds.size === 0) {
      return employees.models
    }
    return employees.models.filter((emp) => filteredEmployeeIds.has(emp.id))
  }, [filteredEmployeeIds, isLoading])

  const employeesWorkingDays = React.useMemo(() => {
    return data.records.reduce<Record<string, string>>((acc, emp) => {
      acc[String(emp.id)] = emp.contractVersions[0]?.workingWeekDays ?? ''
      return acc
    }, {})
  }, [data.records])

  return (
    <Box padding="s16">
      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        style={{
          width: '100%',
          height: 'calc(100vh - 260px)',
          marginBottom: `calc(${theme.dimensions.bodyPadding} * -1)`,
          overflow: 'auto',
          position: 'relative',
          zIndex: '1',
        }}
      >
        <TeamViewTable
          date={date}
          changingData={changingData || isLoading}
          filteredEmployees={filteredEmployees}
          employeesWorkingDays={employeesWorkingDays}
        />
        {isLoadingMore && (
          <Box padding="s16" justifyContent="center" alignItems="center">
            <Spinner />
          </Box>
        )}
        {children}
      </div>
    </Box>
  )
})

const TeamViewPage = ({
  loading,
  children,
  date,
  range,
  onDateRangeChange,
  canSeeCalendar,
  lastLeaveUpdatedInMilliseconds,
}: React.PropsWithChildren<Props>) => {
  const { company } = useCurrentSession()
  const { filters: employeeFilters, hasJobCatalogTreeFeature } = useEmployeeFilters()

  const { loading: changingData } = useUnsafeAsyncData(() => {
    const data = generateFetchData(date, '', false)
    const ms = lastLeaveUpdatedInMilliseconds
    calendarEvents.reset([])

    return Promise.all([
      calendarEvents.fetch({
        data: { ...data, ms },
      }),
    ])
  }, [date, lastLeaveUpdatedInMilliseconds])

  const source = useTeamViewSource(
    company.id,
    hasJobCatalogTreeFeature,
    i18nDateToString(date, 'backend'),
    employeeFilters
  )

  return (
    <ResponsivePage.Body
      authorized={canSeeCalendar}
      showHeader={false}
      loading={loading}
      error={null}
    >
      <Box style={{ position: 'relative' }}>
        <Box
          style={{
            position: 'absolute',
            top: '2px',
            right: '2px',
          }}
          flexDirection="row"
          gap="s16"
          alignItems="center"
        >
          <DateNavigator
            regularPeriods={['month']}
            startDate={range[0]}
            endDate={range[1]}
            onDateRangeChange={(...newRange) => onDateRangeChange(newRange)}
          />
          <Exports />
        </Box>
        <OneDataCollection
          id="calendar/team-view/v1"
          source={source}
          visualizations={[
            {
              label: 'Calendar',
              icon: Calendar,
              type: 'custom',
              component: (props: CustomVisualizationProps<TeamViewSource>) => (
                <TeamViewVisualization
                  source={props.source}
                  date={date}
                  changingData={changingData}
                >
                  {children}
                </TeamViewVisualization>
              ),
            },
          ]}
        />
      </Box>
    </ResponsivePage.Body>
  )
}

export default observer(TeamViewPage)
